import defaultAxiosClient from '../plugins/axios';
import {shiftDtoEndDatePlusOneDayIfPastMidnight, toYear, toYearAndMonth} from "@/util/DateUtil";
import WorkShift from "@/domain/WorkShift";
import PlannedShift from "@/domain/PlannedShift";
import {toAbsenceObject} from "@/constants/Absences";
import Vue from "vue";
import {extractFileNameAndSave} from "@/util/FormattingUtil";

const initialState = {
    plannedShifts: [],
    workShifts: [],
    customersWithShiftTypes: [],
    holidays: [],
    occupiedDays: [],
    completed: false,
    completedMappingObject: {},
    absenceDays: [],
    countedHours: 0,
    neededHours: 0,
    workedEnough: true,
    canAdd: {
        VACATION_FULL_DAY : false,
        VACATION_HALF_DAY : false,
        RECUPERATION_FULL_DAY : false,
        RECUPERATION_HALF_DAY : false,
        EDUCATIONAL_LEAVE_FULL_DAY : false,
        EDUCATIONAL_LEAVE_HALF_DAY : false,
    },
    recupPreviousHours: 0,
    recupTakenHours: 0,
    recupPreviousInDays: 0,
    recupTakenInDays: 0,
    remainingRecupInDays: 0,
    remainingVacationDays: 0,
    remainingEducationalLeaveInDays: 0,
    remainingRecuperationHolidays: 0,
    canAddRecup: false,
    vacationApprovedByAdmin: false,
    recupHours: 0,
    selectedContract: undefined,
    nextMonthAvailable: false,
    previousMonthAvailable: false,
    activeContracts: [],
    locked: false,
    removedRecupHolidays: [],
    removedRecupHoliday: null,
    halfDayCanBeRecuperated: false,
    recupForYearDto: [],
    recupBonuses: []
};
const initialStateForFullCalendar = {
    plannedShifts: [],
    occupiedDays: [],
    absenceDays: [],
};

const timesheet = {
    namespaced: true,
    state() {
        return {...initialState};
    },
    getters: {
        plannedShifts: state => state.plannedShifts,
        workShifts: state => state.workShifts,
        holidays: state => state.holidays,
        occupiedDays: state => state.occupiedDays,
        customersWithShiftTypes: state => state.customersWithShiftTypes,
        completed: state => state.completed,
        completedMappingObject: state => state.completedMappingObject,
        absenceDays: state => state.absenceDays,
        countedHours: state => state.countedHours,
        neededHours: state => state.neededHours,
        workedEnough: state => state.workedEnough,
        canAdd: state => state.canAdd,
        recupForYearDto: state => state.recupForYearDto,
        recupBonuses: state => state.recupBonuses,
        recupPreviousHours: state => state.recupPreviousHours,
        recupTakenHours: state => state.recupTakenHours,
        recupPreviousInDays: state => state.recupPreviousInDays,
        recupTakenInDays: state => state.recupTakenInDays,
        remainingRecupInDays: state => state.remainingRecupInDays,
        remainingVacationDays: state => state.remainingVacationDays,
        remainingEducationalLeaveInDays: state => state.remainingEducationalLeaveInDays,
        remainingRecuperationHolidays: state => state.remainingRecuperationHolidays,
        canAddRecup: state => state.canAdd.RECUPERATION_FULL_DAY || state.canAdd.RECUPERATION_HALF_DAY,
        vacationApprovedByAdmin: state => state.vacationApprovedByAdmin,
        selectedContract: state => state.selectedContract,
        activeContracts: state => state.activeContracts,
        isLocked: state => state.locked,
        nextMonthAvailable: state => state.nextMonthAvailable,
        previousMonthAvailable: state => state.previousMonthAvailable,
        removedRecupHolidays: state => state.removedRecupHolidays,
        removedRecupHoliday: state => state.removedRecupHoliday,
        halfDayCanBeRecuperated: state => state.halfDayCanBeRecuperated,
    },
    mutations: {
        resetState(state) {
            Object.assign(state, initialState);
        },
        resetStateBeforeRenderingFullCalendar(state) {
            Object.assign(state, initialStateForFullCalendar);
        },
        setAllWorkShifts(state, workShifts) {
            const dataToObjects = [];
            let shiftObj;
            workShifts.forEach((customerShift) => {
                customerShift.workedShiftDTOList.forEach((workShift) => {
                    shiftObj = new WorkShift(
                        workShift.shiftTypeId,
                        workShift.labourPeriod,
                        workShift.breakPeriod,
                        workShift.color,
                        workShift.name,
                        customerShift.customerName,
                        customerShift.contractId,
                        workShift.remark,
                        workShift.workedHours,
                        workShift.interrupted,
                        workShift.callout,
                        workShift.editedByAdmin);
                    dataToObjects.push(shiftObj);
                });
            });
            state.workShifts = dataToObjects;
        },
        setPlannedShifts(state, payload) {
            const dataToObjects = [];
            payload.forEach((plannedShift) => {
                if (plannedShift.adjusted === true) {
                    plannedShift.name = '* ' + plannedShift.name;
                }
                const newPlannedShift = new PlannedShift(
                    plannedShift.startDate,
                    plannedShift.endDate,
                    plannedShift.shiftId,
                    plannedShift.color,
                    plannedShift.name,
                    plannedShift.adjusted);
                dataToObjects.push(newPlannedShift);
            });
            state.plannedShifts = dataToObjects;
        },
        setCustomerShiftTypes(state, customers) {
            state.customersWithShiftTypes = customers;
        },
        setHolidays(state, holidays) {
            const dataToObjects = [];
            holidays.forEach(holiday => {
                dataToObjects.push({
                    start: holiday.date,
                    allDay: true,
                    rendering: 'background',
                    color: "red"
                });
            });
            state.holidays = dataToObjects;
        },
        setOccupiedDays(state, payload) {
            const dataToObjects = [];
            payload.forEach(occupiedDay => {
                dataToObjects.push({
                    start: occupiedDay,
                    allDay: true,
                    rendering: 'background',
                    color: "lightGreen"
                });
            });
            state.occupiedDays = dataToObjects;
        },
        setAbsences(state, absenceDtos) {
            let absences = [];
            absenceDtos.forEach(absenceDto => {
                const absence = toAbsenceObject(absenceDto);
                if (absence) {
                    absences.push({
                        start: absenceDto.date,
                        allDay: absence.allDay,
                        color: absence.color,
                        textColor: absence.textColor,
                        absenceCategory: absenceDto.absenceType.absenceCategory,
                        fullDay: absenceDto.absenceType.fullDay,
                        title: absence.title,
                        hours: absenceDto.hours,
                        editedByAdmin: absenceDto.editedByAdmin
                    });
                }
            });
            state.absenceDays = absences;
        },
        setCompleted(state, boolean) {
            state.completed = boolean;
        },
        addToCompletedMappingObject(state, payload) {
            const contractId = payload.contractId;
            const completed = payload.completed;

            Vue.set(state.completedMappingObject, contractId, completed);
        },
        setCountedHours(state, countedHours) {
            state.countedHours = countedHours;
        },
        setNeededHours(state, neededHours) {
            state.neededHours = neededHours;
        },
        setPreviousMonthAvailable(state, previousMonthAvailable) {
            state.previousMonthAvailable = previousMonthAvailable;
        },
        setNextMonthAvailable(state, nextMonthAvailable) {
            state.nextMonthAvailable = nextMonthAvailable;
        },
        setWorkedEnough(state, workedEnough) {
            state.workedEnough = workedEnough;
        },
        setCanAddRecupFullDay(state, canAddRecupFull){
            state.canAdd = {...state.canAdd, RECUPERATION_FULL_DAY : canAddRecupFull};
        },
        setCanAddRecupHalfDay(state, canAddRecupHalf){
            state.canAdd = {...state.canAdd, RECUPERATION_HALF_DAY : canAddRecupHalf};
        },
        setCanAddVacationFullDay(state, canAddVacationFull){
            state.canAdd = {...state.canAdd, VACATION_FULL_DAY : canAddVacationFull};
        },
        setCanAddVacationHalfDay(state, canAddVacationHalf){
            state.canAdd = {...state.canAdd, VACATION_HALF_DAY : canAddVacationHalf};
        },
        setCanAddRecupHolidayFullDay(state, canAddRecupHolidayFull) {
            state.canAdd = {...state.canAdd, RECUPERATION_HOLIDAY: canAddRecupHolidayFull};
        },
        setCanAddEducationalLeaveFullDay(state, CanAddEducationalLeaveFull){
            state.canAdd = {...state.canAdd, EDUCATIONAL_LEAVE_FULL_DAY : CanAddEducationalLeaveFull};
        },
        setCanAddEducationalLeaveHalfDay(state, EducationalLeaveHalf){
            state.canAdd = {...state.canAdd, EDUCATIONAL_LEAVE_HALF_DAY : EducationalLeaveHalf};
        },
        setVacationApprovedByAdmin(state, vacationApprovedByAdmin) {
            state.vacationApprovedByAdmin = vacationApprovedByAdmin;
        },
        setRecupPreviousHours(state, recupPrevious) {
            state.recupPreviousHours = recupPrevious;
        },
        setRecupTakenHours(state, recupTaken) {
            state.recupTakenHours = recupTaken;
        },
        setRecupPreviousInDays(state, recupPreviousInDays) {
            state.recupPreviousInDays = recupPreviousInDays;
        },
        setRecupForYearDto(state, payload){
            state.recupForYearDto = payload;
        },
        setRecupBonusDtos(state, recupbonuses){
            state.recupBonuses = recupbonuses;
        },
        setRecupTakenInDays(state, recupTakenInDays) {
            state.recupTakenInDays = recupTakenInDays;
        },
        setRemainingRecupInDays(state, remainingRecupInDays) {
            state.remainingRecupInDays = remainingRecupInDays;
        },
        setRemainingVacationDays(state, remainingVacationDays) {
            state.remainingVacationDays = remainingVacationDays;
        },
        setRemainingEducationalLeaveInDays(state, remainingEducationalLeaveInDays) {
            state.remainingEducationalLeaveInDays = remainingEducationalLeaveInDays;
        },
        setRemainingRecuperationHolidays(state, remainingRecuperationHolidays) {
            state.remainingRecuperationHolidays = remainingRecuperationHolidays;
        },
        setSelectedContract(state, contract) {
            state.selectedContract = contract;
        },
        setActiveContracts(state, contracts) {
            state.activeContracts = contracts;
        },
        setLocked(state, payload) {
            state.locked = payload;
        },
        setRemovedRecupHolidays(state, payload) {
            state.removedRecupHolidays = payload;
        },
        setRemovedRecupHoliday(state, payload) {
            state.removedRecupHoliday = payload;
        },
        setHalfDayCanBeRecuperated(state, payload) {
            state.halfDayCanBeRecuperated = payload;
        }
    },
    actions: {
        fetchPlannedShifts({commit, state, rootState}) {
            const contractId = state.selectedContract.id;
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);

            return defaultAxiosClient ('/timesheet/' + contractId + '/' + yearMonth + '/planned')
                .then(response => processTimeSheet(commit, response, contractId))
                .then(() => processCanAdd(state, commit, rootState))
                .catch(() => commit('resetState'));
        },
        fetchWorkShifts({commit, state}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);

            return defaultAxiosClient.get('/timesheet/' + yearMonth + '/worked')
                .then(response => {
                    commit('setAllWorkShifts', response.data.workedCustomerShiftDtos);
                    commit('setCompleted', response.data.completed);
                    commit('addToCompletedMappingObject', {
                        contractId: state.selectedContract.id,
                        completed: response.data.completed
                    });
                    commit('setNextMonthAvailable', response.data.nextMonthAvailable);
                    commit('setPreviousMonthAvailable', response.data.previousMonthAvailable);
                }).catch(() => commit('resetState'));
        },
        fetchAbsences({commit, state, rootState}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);

            return defaultAxiosClient.get('/timesheet/' + yearMonth + '/absences')
                .then(response => {
                    processAbsenceSheet(commit, response);
                })
                .then(() => processCanAdd(state, commit, rootState))
                .catch(() => commit('resetState'));
        },
        updateWorkedShift({commit}, {contractId, yearMonth, workedShiftUpdateDto}) {
            workedShiftUpdateDto = shiftDtoEndDatePlusOneDayIfPastMidnight(workedShiftUpdateDto);
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/worked/updateshift', workedShiftUpdateDto)
                .then(response => commit('setAllWorkShifts', response.data.workedCustomerShiftDtos));
        },
        updateWorkedShiftToAbsence({commit}, {contractId, yearMonth, transformWorkedShiftIntoAbsenceDto}) {
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/worked/turnintoabsence', transformWorkedShiftIntoAbsenceDto)
                .then(putResponse => {
                    commit('setAllWorkShifts', putResponse.data.workedCustomerShiftDtos);
                    commit('setRemovedRecupHoliday', putResponse.data.removedRecupHoliday);
                    return defaultAxiosClient.get('/timesheet/' + yearMonth + '/absences')
                        .then(getResponse => commit('setAbsences', getResponse.data.absenceDtos));
                });
        },
        deleteWorkedShift({commit}, {contractId, yearMonth, workedShiftDeleteDto}) {
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/worked/removeshift', workedShiftDeleteDto)
                .then(response => {
                    commit('setAllWorkShifts', response.data.workedCustomerShiftDtos);
                    commit('setRemovedRecupHoliday', response.data.removedRecupHoliday);
                });
        },
        deletePlannedShift({commit}, {contractId, yearMonth, deletePlannedShiftDto}) {
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/planned/removeshift', deletePlannedShiftDto)
                .then(response => {
                        commit('setCountedHours', response.data.timeSheetHoursDto.worked);
                        commit('setPlannedShifts', response.data.plannedShiftDtos);
                        commit('setOccupiedDays', response.data.occupiedDays);
                        return defaultAxiosClient.get('/timesheet/' + yearMonth + '/absences')
                            .then(
                                getResponse => {
                                    commit('setAbsences', getResponse.data.absenceDtos);
                                });

                    }
                );
        },
        postShiftType({commit}, {shift, customerId}) {
            defaultAxiosClient.post(`/shifttype/${customerId}`, shift)
                    .then(response => {
                        const customerDtoList = response.data._embedded?.customerDtoList || [];
                        commit('setCustomerShiftTypes', customerDtoList);
                    });
        },
        editShiftType({commit}, {shift, customerId}) {
            return defaultAxiosClient.put(`/shifttype/${customerId}/${shift.id}`, shift)
                .then(response => {
                    const customerDtoList = response.data._embedded?.customerDtoList || [];
                    commit('setCustomerShiftTypes', customerDtoList);
                });
        },
        postPlannedShift({commit, state, rootState}, {plannedShift, yearMonth, contractId}) {
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/planned/addshift', plannedShift)
                .then(response => processTimeSheet(commit, response, contractId))
                .then(() => processCanAdd(state, commit, rootState));
        },
        postPlannedShifts({commit, state, rootState}, {yearMonth, createPlannedShiftsDto}) {
            const contractId = state.selectedContract.id;
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/planned/addshifts', createPlannedShiftsDto)
                .then(response => processTimeSheet(commit, response, contractId))
                .then(() => processCanAdd(state, commit, rootState));
        },
        updatePlannedShift({commit, state, rootState}, {yearMonth, editPlannedShiftDto}) {
            const contractId = state.selectedContract.id;
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/planned/updateshift', editPlannedShiftDto)
                .then(response => processTimeSheet(commit, response, contractId))
                .then(() => processCanAdd(state, commit, rootState));
        },
        fetchShiftsOrderedByCustomer({commit}) {
            return defaultAxiosClient.get('/shifttype/customershifttypes')
                .then(response => {
                    const customerDtoList = response.data._embedded?.customerDtoList || [];
                    commit('setCustomerShiftTypes', customerDtoList);
                    return customerDtoList;
                })
                .catch(() => commit('resetState'));
        },
        fetchContracts({dispatch}) {
            return dispatch('getAllActiveContracts');
        },
        fetchPlannedData({dispatch}) {
            return dispatch('fetchPlannedShifts');
        },
        fetchAbsenceData({dispatch}) {
            return dispatch('fetchAbsences');
        },
        completeTimeSheet({commit, state}, yearMonth) {
            const contractId = state.selectedContract.id;
            defaultAxiosClient.put('/timesheet/' + yearMonth + '/worked/complete')
                .then(response => {
                    const completed = response.data.completed;
                    commit('setCompleted', completed);
                    commit('addToCompletedMappingObject', {
                        contractId,
                        completed
                    });
                });
        },
        lockPlanning({commit}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);
            defaultAxiosClient.put('/timesheet/' + yearMonth + '/lock')
                .then(() => commit('setLocked', true));
        },
        addWorkedShift({commit}, {contractId, yearMonth, createWorkedShiftDto}) {
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/worked/createshift', createWorkedShiftDto)
                .then(response => commit('setAllWorkShifts', response.data.workedCustomerShiftDtos));
        },
        addAbsence({commit, state, rootState}, {yearMonth, createAbsenceDto}) {
            defaultAxiosClient.put('/timesheet/' + yearMonth + '/planned/addabsence', createAbsenceDto)
                .then(response => processAbsenceSheet(commit, response))
                .then(() => processCanAdd(state, commit, rootState));
        },
        addAbsences({commit, state, rootState}, {yearMonth, createAbsencesDto}) {
            defaultAxiosClient.put('/timesheet/' + yearMonth + '/planned/addabsences', createAbsencesDto)
                .then(response => processAbsenceSheet(commit, response))
                .then(() => processCanAdd(state, commit, rootState));
        },
        removeAbsence({commit, state, rootState}, {yearMonth, removeAbsenceDto}) {
            defaultAxiosClient.put('/timesheet/' + yearMonth + '/planned/removeabsence', removeAbsenceDto)
                .then(response => processAbsenceSheet(commit, response))
                .then(() => processCanAdd(state, commit, rootState));
        },
        getAllActiveContractsAndPickSelected({commit}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);
            return defaultAxiosClient.get(`/contract/${yearMonth}/all`)
                .then(response => {
                    const contractWithShiftTypesOutputDtoList = response.data._embedded?.contractWithShiftTypesDtoList	 || [];
                    commit('setActiveContracts', contractWithShiftTypesOutputDtoList);
                    commit('setSelectedContract',
                        contractWithShiftTypesOutputDtoList.length >= 1 ?
                            contractWithShiftTypesOutputDtoList[0] :
                            undefined
                    );
                })
                .catch(() => commit('resetState'));
        },
        getAllActiveContracts({commit}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);
            return defaultAxiosClient.get(`/contract/${yearMonth}/all`)
                .then(response => {
                    const contractWithShiftTypesOutputDtoList = response.data._embedded?.contractWithShiftTypesDtoList	 || [];
                    commit('setActiveContracts', contractWithShiftTypesOutputDtoList);
                })
                .catch(() => commit('resetState'));
        },
        removeAllShifts({commit, state, rootState}, yearMonth) {
            const contractId = state.selectedContract.id;
            return defaultAxiosClient.put('/timesheet/' + contractId + '/' + yearMonth + '/planned/removeall')
                .then(response => {
                    processTimeSheet(commit, response, contractId);
                    return defaultAxiosClient.get('/timesheet/' + yearMonth + '/absences')
                        .then(
                            getResponse => {
                                commit('setAbsences', getResponse.data.absenceDtos);
                            });
                })
                .then(() => processCanAdd(state, commit, rootState));
        },
        downloadTimeSheet({commit}, contractId) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);
            const uri = `/timesheet/${contractId}/${yearMonth}/download`;

            return defaultAxiosClient.get(uri, {
                responseType: "arraybuffer",
                headers: {
                    'Accept': 'application/pdf',
                }
            }).then((response) => {
                const blob = new Blob([response.data], {type: 'application/pdf'});
                extractFileNameAndSave(response, blob);
            })
                .catch(commit('resetState'));
        },
        fetchRecupForYear({commit}) {
            const year = toYear(this.state.time.currentlySelectedMonth);
            return defaultAxiosClient.get(`/recup/${year}`)
                .then(response => {
                    const recupForMonthDtos = response.data.recupForMonthDtos;
                    const recupBonuses = response.data.recupBonusDtos;
                    commit('setRecupForYearDto', recupForMonthDtos);
                    commit('setRecupBonusDtos',recupBonuses);
                }).catch(() => commit('resetState'));
        }
    }
};

function processAbsenceSheet(commit, response) {
    commit('setAbsences', response.data.absenceDtos);
    commit('setHolidays', response.data.holidayDtos);
    commit('setRecupTakenHours', response.data.recupHoursTaken);
    commit('setCountedHours', response.data.countedHours);
    commit('setRecupTakenInDays', response.data.recupTakenInDays);
    commit('setRemainingVacationDays', response.data.remainingVacationDays);
    commit('setRemainingRecupInDays', response.data.remainingRecupInDays);
    commit('setRemainingEducationalLeaveInDays', response.data.remainingEducationalLeaveInDays);
    commit('setVacationApprovedByAdmin', response.data.vacationApprovedByAdmin);
    commit('setHalfDayCanBeRecuperated', response.data.halfDayCanBeRecuperated);
}

function processTimeSheet(commit, response, contractId) {
    commit('setPlannedShifts', response.data.plannedShiftDtos);
    commit('setCompleted', response.data.completed);
    commit('addToCompletedMappingObject', {
        completed: response.data.completed,
        contractId: contractId
    });
    commit('setOccupiedDays', response.data.occupiedDays);
    commit('setCountedHours', response.data.timeSheetHoursDto.worked);
    commit('setNeededHours', response.data.timeSheetHoursDto.needed);
    commit('setWorkedEnough', response.data.timeSheetHoursDto.workedEnough);
    commit('setRecupPreviousHours', response.data.timeSheetHoursDto.recupPrevious);
    commit('setRecupTakenHours', response.data.timeSheetHoursDto.recupTaken);
    commit('setRecupPreviousInDays', response.data.timeSheetHoursDto.recupPreviousInDays);
    commit('setRecupTakenInDays', response.data.timeSheetHoursDto.recupTakenInDays);
    commit('setLocked', response.data.locked);
    commit('setPreviousMonthAvailable', response.data.previousMonthAvailable);
    commit('setNextMonthAvailable', response.data.nextMonthAvailable);
}

function processCanAdd(state, commit, rootState){
    commit('setCanAddRecupFullDay', state.recupTakenHours + rootState.user.currentLoggedUser.sickDayHours <= state.recupPreviousHours);
    commit('setCanAddRecupHalfDay', state.recupTakenHours + (rootState.user.currentLoggedUser.sickDayHours * 0.5) <= state.recupPreviousHours);
    commit('setCanAddVacationFullDay', state.remainingVacationDays >= 1);
    commit('setCanAddVacationHalfDay', state.remainingVacationDays >= 0.5 && state.halfDayCanBeRecuperated);
    commit('setCanAddEducationalLeaveHalfDay', state.remainingEducationalLeaveInDays >= 0.5);
    commit('setCanAddEducationalLeaveFullDay', state.remainingEducationalLeaveInDays >= 1);
}

export default timesheet;